import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import { getUserData } from "../../utils/authnicate";
import "./index.scss";

// ── Greeting helpers ──────────────────────────────────────────────────────────
const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const getRoleLabel = (user) => {
  if (!user) return "";
  const roles = user.roles || (user.role ? [user.role] : []);
  if (!roles.length) return "";
  const role = roles[0];
  const map = { Admin: "Administrator", Spoc: "SPOC", Finance: "Finance Team" };
  return map[role] || role;
};

const getUserName = (user) => {
  if (!user) return "User";
  return (
    user.name ||
    user.userName ||
    user.username ||
    user.email?.split("@")[0] ||
    "User"
  );
};

const AUTO_CLOSE_MS = 10_000; // 10 seconds

/**
 * HomeStrip — shows once per user session.
 * - Checks localStorage key `greeting_shown_<userId>` — if present, never mounts the alert.
 * - Auto-closes after 10 s with a progress indicator.
 * - Manual close button also dismisses it.
 * - No "Re-open" button — once gone, it's gone for this user.
 */
const HomeStrip = () => {
  const user = getUserData();
  const userId = user?.id || user?.userId || user?.email || "guest";
  const storageKey = `greeting_shown_${userId}`;

  // If already shown before → never render
  const alreadyShown = !!localStorage.getItem(storageKey);

  const [open, setOpen] = useState(!alreadyShown);
  const [countdown, setCountdown] = useState(AUTO_CLOSE_MS / 1000); // seconds
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  const dismiss = () => {
    localStorage.setItem(storageKey, "1");
    setOpen(false);
    clearTimeout(timerRef.current);
    clearInterval(countdownRef.current);
  };

  useEffect(() => {
    if (alreadyShown || !open) return;

    // Auto-close after 10s
    timerRef.current = setTimeout(dismiss, AUTO_CLOSE_MS);

    // Countdown tick every second
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (alreadyShown) return null;

  const greeting = getGreeting();
  const name = getUserName(user);
  const roleLabel = getRoleLabel(user);

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Collapse in={open} timeout={400}>
        <Alert
          severity="success"
          variant="outlined"
          className="hs-alert"
          action={
            <div className="hs-alert__right">
              {/* <span className="hs-countdown">{countdown}s</span> */}
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={dismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </div>
          }
          sx={{
            alignItems: "center",
            background: "linear-gradient(90deg, #f0fdf4 0%, #eff6ff 100%)",
            borderColor: "#86efac",
            borderRadius: "10px",
          }}
        >
          <span className="hs-alert__text">
            {greeting}, <strong>{name}</strong>!
            {roleLabel && (
              <>
                {" "}
                Welcome back as <span className="hs-role">{roleLabel}</span>.
              </>
            )}{" "}
            Have a great day! 👋
          </span>
        </Alert>
      </Collapse>
    </Box>
  );
};

export default HomeStrip;
